// Airtable Configuration
// Replace these with your actual Airtable credentials
const AIRTABLE_API_KEY = 'YOUR_AIRTABLE_API_KEY' // Get from: airtable.com/create/tokens
const AIRTABLE_BASE_ID = 'YOUR_BASE_ID' // Get from your base URL (starts with 'app')
const AIRTABLE_TABLE_NAME = 'Users'

const AIRTABLE_API_URL = `https://api.airtable.com/v0/${AIRTABLE_BASE_ID}/${AIRTABLE_TABLE_NAME}`
const WEB3FORMS_KEY = '9d55d263-83bb-4e6f-b05a-3b8d38836fdc'

interface AirtableUser {
  id: string
  fields: {
    Name: string
    Email: string
    Type: 'Brand' | 'Organizer'
    AccessCode: string
    Status: 'Waitlist' | 'Approved' | 'Active'
    CreatedAt: string
  }
}

// Generate a unique, memorable access code based on user info
export function generateAccessCode(
  type: 'Brand' | 'Organizer',
  name: string,
  industry?: string,
): string {
  // Prefix based on type
  const prefix = type === 'Brand' ? 'BR' : 'EV'

  // Clean and extract meaningful part of name (first word, max 6 chars)
  const cleanName = name
    .split(/[\s\-_&]+/)[0] // Get first word
    .replace(/[^a-zA-Z0-9]/g, '')
    .substring(0, 6)
    .toUpperCase()

  // Industry/type abbreviation (2-3 chars)
  const industryMap: Record<string, string> = {
    Technology: 'TEC',
    Fashion: 'FSH',
    'Food & Beverage': 'FNB',
    Finance: 'FIN',
    'Health & Wellness': 'HLT',
    Entertainment: 'ENT',
    Automotive: 'AUT',
    Sports: 'SPT',
    Travel: 'TRV',
    Conference: 'CNF',
    Festival: 'FST',
    'Sports Event': 'SPT',
    Concert: 'MUS',
    'Trade Show': 'TRD',
    'Community Event': 'COM',
    'Charity Event': 'CHR',
    'Corporate Event': 'CRP',
    Other: 'OTH',
  }
  const industryCode = industry ? industryMap[industry] || 'GEN' : 'GEN'

  // Year + random suffix for uniqueness
  const year = new Date().getFullYear().toString().slice(-2)
  const random = Math.random().toString(36).substring(2, 5).toUpperCase()

  // Format: BR-ACME-TEC-24X7K or EV-SUMMIT-CNF-24A3B
  return `${prefix}-${cleanName}-${industryCode}-${year}${random}`
}

// Generate a simpler, more user-friendly code for returning users
export function generateSimpleCode(name: string): string {
  const cleanName = name
    .split(/[\s\-_&]+/)[0]
    .replace(/[^a-zA-Z0-9]/g, '')
    .substring(0, 4)
    .toUpperCase()
  const numbers = Math.floor(1000 + Math.random() * 9000)
  return `${cleanName}${numbers}`
}

// Register a new user and generate their access code
export async function registerUser(data: {
  name: string
  email: string
  type: 'Brand' | 'Organizer'
  industry?: string
  profileData?: any
}): Promise<{ success: boolean; accessCode: string; error?: string }> {
  const accessCode = generateAccessCode(data.type, data.name, data.industry)

  // Always generate a code, even if Airtable isn't configured
  if (AIRTABLE_API_KEY === 'YOUR_AIRTABLE_API_KEY') {
    console.log('Airtable not configured - using local code generation')

    // Send email notification via Web3Forms
    try {
      await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `🎉 New ${data.type} Registration: ${data.name}`,
          from_name: 'SponsrAI Registration',
          to: 'info@sponsrai.se',

          'Registration Type': `${data.type} (Full Profile)`,
          'Generated Access Code': accessCode,
          Name: data.name,
          Email: data.email,
          'Industry/Type': data.industry || 'Not specified',
          'Profile Data': data.profileData
            ? JSON.stringify(data.profileData, null, 2)
            : 'Not provided',
          'Registered At': new Date().toISOString(),
        }),
      })
    } catch (err) {
      console.error('Email notification failed:', err)
    }

    return { success: true, accessCode }
  }

  try {
    // Save to Airtable
    const response = await fetch(AIRTABLE_API_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${AIRTABLE_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        records: [
          {
            fields: {
              Name: data.name,
              Email: data.email,
              Type: data.type,
              AccessCode: accessCode,
              Status: 'Active', // Auto-approve since they completed full signup
              CreatedAt: new Date().toISOString().split('T')[0],
            },
          },
        ],
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Airtable error:', error)
      return { success: false, accessCode, error: 'Failed to save to database' }
    }

    // Send email notification
    await fetch('https://api.web3forms.com/submit', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        access_key: WEB3FORMS_KEY,
        subject: `🎉 New ${data.type} Registration: ${data.name}`,
        from_name: 'SponsrAI Registration',
        to: 'info@sponsrai.se',

        'Registration Type': `${data.type} (Full Profile)`,
        'Generated Access Code': accessCode,
        Name: data.name,
        Email: data.email,
        'Industry/Type': data.industry || 'Not specified',
        'Registered At': new Date().toISOString(),
      }),
    })

    return { success: true, accessCode }
  } catch (err) {
    console.error('Registration error:', err)
    return { success: false, accessCode, error: 'Registration failed' }
  }
}

// Add a new user to Airtable (called when someone joins waitlist)
export async function addUserToAirtable(data: {
  name: string
  email: string
  type: 'Brand' | 'Organizer'
}): Promise<{ success: boolean; accessCode?: string; error?: string }> {
  // Skip if Airtable is not configured
  if (AIRTABLE_API_KEY === 'YOUR_AIRTABLE_API_KEY') {
    console.log('Airtable not configured - skipping database save')
    const accessCode = generateAccessCode(data.type, data.name)
    return { success: true, accessCode }
  }

  const accessCode = generateAccessCode(data.type, data.name)

  try {
    const response = await fetch(AIRTABLE_API_URL, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${AIRTABLE_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        records: [
          {
            fields: {
              Name: data.name,
              Email: data.email,
              Type: data.type,
              AccessCode: accessCode,
              Status: 'Waitlist',
              CreatedAt: new Date().toISOString().split('T')[0],
            },
          },
        ],
      }),
    })

    if (!response.ok) {
      const error = await response.json()
      console.error('Airtable error:', error)
      return { success: false, error: 'Failed to save to database' }
    }

    return { success: true, accessCode }
  } catch (err) {
    console.error('Airtable error:', err)
    return { success: false, error: 'Database connection failed' }
  }
}

// Validate an access code against Airtable
export async function validateAccessCode(code: string): Promise<{
  valid: boolean
  user?: { name: string; email: string; type: string; status: string }
  error?: string
}> {
  // Skip if Airtable is not configured - fall back to pattern matching
  if (AIRTABLE_API_KEY === 'YOUR_AIRTABLE_API_KEY') {
    // Accept any code that matches our format: XX-NAME-XXX-XXXX
    const codePattern = /^(BR|EV)-[A-Z0-9]+-[A-Z]{3}-\d{2}[A-Z0-9]{3}$/i
    const legacyCodes = ['sponsrai2024', 'brand-early-2024', 'org-early-2024']

    if (codePattern.test(code) || legacyCodes.includes(code.toLowerCase())) {
      return { valid: true }
    }
    return { valid: false, error: 'Invalid access code format' }
  }

  try {
    const filterFormula = encodeURIComponent(`{AccessCode} = '${code}'`)
    const response = await fetch(
      `${AIRTABLE_API_URL}?filterByFormula=${filterFormula}`,
      {
        headers: {
          Authorization: `Bearer ${AIRTABLE_API_KEY}`,
        },
      },
    )

    if (!response.ok) {
      console.error('Airtable validation error')
      return { valid: false, error: 'Validation failed' }
    }

    const data = await response.json()

    if (data.records && data.records.length > 0) {
      const user = data.records[0].fields

      if (user.Status === 'Approved' || user.Status === 'Active') {
        return {
          valid: true,
          user: {
            name: user.Name,
            email: user.Email,
            type: user.Type,
            status: user.Status,
          },
        }
      } else {
        return {
          valid: false,
          error:
            "Your access is pending approval. We'll email you when you're approved!",
        }
      }
    }

    return { valid: false, error: 'Invalid access code' }
  } catch (err) {
    console.error('Airtable error:', err)
    // Fall back to pattern matching
    const codePattern = /^(BR|EV)-[A-Z0-9]+-[A-Z]{3}-\d{2}[A-Z0-9]{3}$/i
    if (codePattern.test(code)) {
      return { valid: true }
    }
    return { valid: false, error: 'Validation failed' }
  }
}

// Update user status in Airtable
export async function updateUserStatus(
  accessCode: string,
  status: 'Approved' | 'Active',
): Promise<boolean> {
  if (AIRTABLE_API_KEY === 'YOUR_AIRTABLE_API_KEY') {
    return true
  }

  try {
    const filterFormula = encodeURIComponent(`{AccessCode} = '${accessCode}'`)
    const searchResponse = await fetch(
      `${AIRTABLE_API_URL}?filterByFormula=${filterFormula}`,
      {
        headers: {
          Authorization: `Bearer ${AIRTABLE_API_KEY}`,
        },
      },
    )

    const searchData = await searchResponse.json()

    if (!searchData.records || searchData.records.length === 0) {
      return false
    }

    const recordId = searchData.records[0].id

    const updateResponse = await fetch(`${AIRTABLE_API_URL}/${recordId}`, {
      method: 'PATCH',
      headers: {
        Authorization: `Bearer ${AIRTABLE_API_KEY}`,
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        fields: { Status: status },
      }),
    })

    return updateResponse.ok
  } catch (err) {
    console.error('Airtable update error:', err)
    return false
  }
}
